import React from 'react';
import Typography from 'components/commercetools-ui/atoms/typography';
import { NationalBook } from 'fonts';
import { useAdditionalCss } from '../helpers/useAdditionalCss';
import { sanitizeHtml, SECTION_SPACING } from '../helpers/rendering';
import Timeline from './timeline';
import type { OurStorySectionContent } from './types';

/**
 * Our Story Section ← Methven `multiple_info_section`: a heading beside the company timeline.
 *
 * This is a `sections` block in its own right, not a `generic_section` rendering, because the CMS
 * models it that way: `our_story_section` has its own `multiple_info` group with a nested
 * `content_with_icon`, and nothing in `generic_section`'s `cards` can carry that second level.
 * Before it existed the migration flattened the timeline into Chronos and dropped every detail
 * panel.
 *
 * Ported from `organisms/our-story/index.tsx` (lines 67-79) plus `atoms/timeline`, which is what
 * the live /our-story page renders through the `commercetools/ui/our-story` tastic. Two deliberate
 * changes: the V1 wrapper's `-mt-48 pb-48` is gone, since spacing between sections is now carried
 * by SECTION_SPACING; and the heading is an h2, because in V1 it was a second h1 on a page whose
 * hero already has one.
 */
export default function OurStorySection({ content }: { content: OurStorySectionContent }) {
  const { title, description, multiple_info = [], additional_css } = content ?? {};
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'OurStorySection');

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} items-center lg:flex ${uniqueClass ?? ''}`}>
        <div className="px-24 xl:pl-90 2xl:pl-180">
          {title && (
            <Typography
              as="h2"
              className="max-w-[40rem] text-center text-28 font-semibold leading-[3rem] text-primary-black drop-shadow-sm lg:text-left 3xl:text-40"
            >
              {title}
            </Typography>
          )}
          {description && (
            <div
              className={`mt-16 max-w-[40rem] text-center text-16 font-normal leading-6 text-[#333] lg:text-left ${NationalBook.className}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
            />
          )}
        </div>
        <div className="flex-1 lg:h-[15rem] lg:w-[45rem] lg:px-48 xl:px-90 3xl:h-[20rem]">
          <Timeline milestones={multiple_info} />
        </div>
      </div>
    </>
  );
}
